import { setLoading } from 'actions';
import {
  setOrders,
  clearOrders,
  setRbiRefundsCount,
  setRbiRutrEvents,
  setRbiCustomerId,
  setRbiLoyaltyId,
  setRbiCouponsInfo,
  setNumberSupportTransactions,
  setRbiCustomerBirthday,
} from 'actions/tools/OrdersSearchActions';
import {
  RBI_SET_STORES,
  RBI_SET_SELECTED_STORE,
  RBI_SET_STORE_LOCATION_QUERY,
  RBI_CLEAR_STATE,
} from 'constants/rbiActionTypes';
import { showExpressError } from 'util/errorPopupUtil';
import { rbiGetRequest, rbiPostRequest } from 'api/orders/rbi';
import { setRbiApprovedRefundAmount } from 'actions/tools/OrdersSearchActions';
import {
  getActionReasons,
  extractSelectedTicketFromState,
  getBrandOfSelectedTicket,
  getSelectedRbiItemsForRefund,
  getSimplrAccountIdOfSelectedTicket,
  getSelectedOrderFromOrdersSearch,
  getRefundAmountDetails,
  getDecisionNodeAtPath,
  getRbiCustomerId,
  getRbiLoyaltyId,
  getRbiLoyaltyCardId,
  getLegacyCognitoId,
  getPointsPrimaryReason,
  getSelectedRbiItemsForCoupon,
  getSelectedRbiItemsForLoyaltyPoints,
  getSelectedNestedSubItems,
} from 'extractFromState';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { constructRbiCouponOrLoyaltyPointsItemsList, emailRegex } from '../../util';
import { displayPopupMessageInformation } from '../../components/tools/SearchPage/searchUtils';
import { SET_RBI_CUSTOMER_INFO, SET_RBI_GIFT_CARD_TRANSFER } from '../types';
import makeRequest from '../../api/makeRequest';
import environment from '../../environment';
import { addEdgeToDecisionTreePath } from '../searchActions';
import { handleError } from 'services/ErrorService/ErrorService';
import { getSelectedLineItemsIds, getSelectedOrderId } from '../../selectors/tools';
import { setRbiAssignedOffers } from './OrdersSearchActions';

const buildSearchQuery =
  (mainQuery, simplrAccountPtrId, domain = null, brandName = null) =>
  (dispatch, getState) => {
    const state = getState();
    const query = {
      ...mainQuery,
      simplrAccountId: simplrAccountPtrId,
    };

    if (!domain && !brandName) {
      brandName = getBrandOfSelectedTicket(state);
      if (!brandName) {
        throw new Error('Unknown brand name');
      }

      query.brandName = brandName;
    } else {
      if (domain) {
        query.domain = domain;
      }

      if (brandName) {
        query.brandName = brandName;
      }
    }

    return query;
  };

const SearchRbiOrders = (action) => async (dispatch) => {
  dispatch(clearOrders());
  dispatch(setLoading(true, 'searchPage'));

  try {
    await action();
  } catch (err) {
    if (err && err.status === 404) {
      handleError({ error: err, customMessage: 'Error getting RBI orders' });

      return dispatch(setOrders([]));
    }

    handleError({ error: err, customMessage: 'Error getting customer orders. Please try again.', showPopup: true });
  } finally {
    dispatch(setLoading(false, 'searchPage'));
  }
};

export const searchRbiOrdersById =
  (simplrAccountPtrId, id, domain = null, brandName = null) =>
  async (dispatch) => {
    return dispatch(
      SearchRbiOrders(async () => {
        const query = dispatch(buildSearchQuery({}, simplrAccountPtrId, domain, brandName));
        const { order } = await rbiGetRequest(`/orders/${id}`, query);
        dispatch(setOrders([order]));
      })
    );
  };

export const searchRbiOrdersByEmail =
  (simplrAccountPtrId, email, domain = null, brandName = null) =>
  async (dispatch) => {
    return dispatch(
      SearchRbiOrders(async () => {
        const query = dispatch(
          buildSearchQuery(
            {
              email,
            },
            simplrAccountPtrId,
            domain,
            brandName
          )
        );

        const {
          orders,
          customerId,
          loyaltyId,
          couponRedemptions,
          couponTokens,
          loyaltyInfo,
          totalOrdersCount,
          refundOrdersCount,
          refundOrdersCountInLastFourWeeks,
          refundedPercentage,
          rutrEvents,
          numberCustomerSupportTransactions,
          assignedOffers,
        } = await rbiGetRequest('/orders', query);

        const refundsCount = {
          totalOrdersCount,
          refundOrdersCount,
          refundOrdersCountInLastFourWeeks,
          refundedPercentage,
        };

        dispatch(setRbiCouponsInfo({ couponRedemptions, couponTokens, loyaltyInfo }));
        dispatch(setRbiCustomerId(customerId));
        dispatch(setRbiLoyaltyId(loyaltyId));
        dispatch(setRbiRefundsCount(refundsCount));
        dispatch(setNumberSupportTransactions(numberCustomerSupportTransactions));
        dispatch(setRbiRutrEvents(rutrEvents));
        dispatch(setRbiAssignedOffers(assignedOffers));
        dispatch(setOrders(orders));
      })
    );
  };

export const searchRbiOrdersByCardId =
  (simplrAccountPtrId, cardId, domain = null, brandName = null) =>
  async (dispatch) => {
    return dispatch(
      SearchRbiOrders(async () => {
        const query = dispatch(
          buildSearchQuery(
            {
              cardId,
            },
            simplrAccountPtrId,
            domain,
            brandName
          )
        );

        const {
          orders,
          customerId,
          loyaltyId,
          couponRedemptions,
          couponTokens,
          loyaltyInfo,
          totalOrdersCount,
          refundOrdersCount,
          refundOrdersCountInLastFourWeeks,
          rutrEvents,
          assignedOffers,
        } = await rbiGetRequest('/orders', query);

        const refundsCount = {
          totalOrdersCount,
          refundOrdersCount,
          refundOrdersCountInLastFourWeeks,
        };

        dispatch(setRbiCouponsInfo({ couponRedemptions, couponTokens, loyaltyInfo }));
        dispatch(setRbiCustomerId(customerId));
        dispatch(setRbiLoyaltyId(loyaltyId));
        dispatch(setRbiRefundsCount(refundsCount));
        dispatch(setRbiRutrEvents(rutrEvents));
        dispatch(setRbiAssignedOffers(assignedOffers));
        dispatch(setOrders(orders));
      })
    );
  };

/**
 * NOTE(apoorvyadav1|spd-11043|2024-11-01): if we have items, return it without
 * generating the new list. In case, the conversation other than issue coupon/ points,
 * led to issuing points/coupons, we construct the array and return it
 */
const validateOrConstructRbiCouponOrLoyaltyPointsItemsList = (state, items) => {
  if (items.length) {
    return items;
  }
  const selectedOrder = getSelectedOrderFromOrdersSearch(state);
  const selectedItemsIds = getSelectedLineItemsIds(state);
  const selectedNestedSubItems = getSelectedNestedSubItems(state);

  return constructRbiCouponOrLoyaltyPointsItemsList(selectedOrder, selectedItemsIds, selectedNestedSubItems);
};

// constructs request object for applying a coupon
const constructCouponRequestObject = (state) => {
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  const selectedTicket = extractSelectedTicketFromState(state);
  const conversationId = selectedTicket && selectedTicket.ticketId;
  const brandName = getBrandOfSelectedTicket(state);
  const { couponPrimaryReason, couponSecondaryReason } = getActionReasons(state);
  const userId = getRbiCustomerId(state);
  const decisionNode = getDecisionNodeAtPath(state);
  const couponCodeTypeId = get(decisionNode, 'couponType.id', null);
  const selectedOrder = getSelectedOrderFromOrdersSearch(state);
  const selectedRbiItemsForCoupon = validateOrConstructRbiCouponOrLoyaltyPointsItemsList(
    state,
    getSelectedRbiItemsForCoupon(state)
  );

  const requestObject = {
    simplrAccountId,
    conversationId,
    brandName,
    count: 1,
    couponCodeTypeId,
    reason: couponPrimaryReason,
    comments: couponSecondaryReason || '',
    userId,
    orderId: get(selectedOrder, 'rbiOrderId', ''),
    couponItems: selectedRbiItemsForCoupon,
  };

  for (const param in requestObject) {
    if (!requestObject[param] && param !== 'comments' && param !== 'orderId') {
      throw new Error(`Missing param ${param}`);
    }
  }

  return requestObject;
};

// used for issuing Tim Hortons loyalty points and BK/PLK Rewards
const constructIssueLoyaltyPointsRequestObject = (state) => {
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  const brandName = getBrandOfSelectedTicket(state);
  const customerId = getRbiCustomerId(state);
  const legacyCognitoId = getLegacyCognitoId(state);
  const cardId = getRbiLoyaltyCardId(state);
  const rewardsId = getRbiLoyaltyId(state);
  const decisionNode = getDecisionNodeAtPath(state);
  const pointsAmount = parseInt(decisionNode.pointsAmount) || null;
  const selectedTicket = extractSelectedTicketFromState(state);
  const conversationId = selectedTicket && selectedTicket.ticketId;
  const reason = getPointsPrimaryReason(state);
  const orderId = getSelectedOrderId(state);
  const selectedRbiItemsForLoyaltyPoints = validateOrConstructRbiCouponOrLoyaltyPointsItemsList(
    state,
    getSelectedRbiItemsForLoyaltyPoints(state)
  );

  const requestObject = {
    simplrAccountId,
    brandName,
    customerId,
    legacyCognitoId,
    cardId,
    rewardsId,
    points: pointsAmount,
    conversationId,
    reason,
    orderId,
    loyaltyPointItems: selectedRbiItemsForLoyaltyPoints,
  };

  const optionalParams = ['cardId', 'rewardsId', 'legacyCognitoId', 'reason', 'orderId'];

  for (const param in requestObject) {
    if (!requestObject[param] && !optionalParams.includes(param)) {
      throw new Error(`Missing param ${param}`);
    }
  }

  return requestObject;
};

// constructs request object for full or partial refund
const contructRefundRequestObject = (state) => {
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  const selectedTicket = extractSelectedTicketFromState(state);
  const conversationId = selectedTicket && selectedTicket.ticketId;
  const brandName = getBrandOfSelectedTicket(state);
  const selectedOrder = getSelectedOrderFromOrdersSearch(state);
  const actionReasons = getActionReasons(state);
  const selectedItemsForRefund = getSelectedRbiItemsForRefund(state);
  const refundAmountDetails = getRefundAmountDetails(state);
  const loyaltyId = getRbiLoyaltyId(state);
  const customerId = getRbiCustomerId(state);

  const requiredObjects = { selectedOrder, actionReasons, refundAmountDetails };

  for (const obj in requiredObjects) {
    if (isEmpty(requiredObjects[obj])) {
      throw new Error(`${obj} is empty`);
    }
  }

  const { refundPrimaryReason, refundSecondaryReason } = actionReasons;
  const { userDetails, userId, rbiOrderId } = selectedOrder;
  const customer = {
    email: userDetails.email,
    name: userDetails.name,
    id: userId || customerId,
  };

  const requestObject = {
    brandName,
    conversationId,
    customer,
    simplrAccountId,
    rbiOrderId,
    primaryReason: refundPrimaryReason,
  };
  const optionalRequestObject = {};

  if (selectedItemsForRefund) {
    requestObject.refundedItems = selectedItemsForRefund;
  }

  if (loyaltyId && refundAmountDetails.totalPoints) {
    requestObject.loyaltyId = loyaltyId;
    requestObject.points = refundAmountDetails.totalPoints;
  }

  // allow refund if no totalCents but have totalPoints
  if (!refundAmountDetails.totalCents && refundAmountDetails.totalPoints) {
    optionalRequestObject.requestedAmountCents = refundAmountDetails.totalCents;
  } else {
    requestObject.requestedAmountCents = refundAmountDetails.totalCents;
  }

  for (const field in requestObject) {
    if (isNil(requestObject[field]) && !Object.keys(optionalRequestObject).includes(field)) {
      throw new Error(`${field} is undefined`);
    }
  }

  // if secondary reason was not specified
  if (refundSecondaryReason) {
    requestObject.secondaryReason = refundSecondaryReason;
  } else {
    requestObject.secondaryReason = '';
  }

  return Object.assign({}, requestObject, optionalRequestObject);
};

/**
 * api call that process full or partial refund and returns approved refund amount
 * @param {object} requestObject
 * @returns {Promise<number>} approved refund amount
 */
const rbiRefundRequest = async (requestObject) => {
  const { approvedAmount } = await rbiPostRequest('/refund', requestObject);

  if (isNil(approvedAmount) || isNaN(approvedAmount)) {
    throw new Error('Requested refund amount has not been approved.');
  }

  return approvedAmount;
};

// handles refunds from the decision tree
export const processRbiItemsRefund = () => async (dispatch, getState) => {
  const requestObject = contructRefundRequestObject(getState());
  const approvedAmount = await rbiRefundRequest(requestObject);

  dispatch(setRbiApprovedRefundAmount(approvedAmount));
};

/**
 * api call applies coupon to the customer
 * @param {object} requestObject
 * @returns {Promise<void>}
 */
const rbiIssueCouponRequest = async (requestObject) => {
  await rbiPostRequest('/offers', requestObject);
};

const rbiIssueLoyaltyPointsRequest = async (requestObject) => {
  await rbiPostRequest('/issueLoyaltyPoints', requestObject);
};

// handles coupon issue from the decision tree
export const processRbiCouponIssue = () => async (dispatch, getState) => {
  const state = getState();
  const requestObject = constructCouponRequestObject(state);
  await rbiIssueCouponRequest(requestObject);
};

export const processRbiIssueLoyaltyPoints = () => async (dispatch, getState) => {
  const state = getState();
  const requestObject = constructIssueLoyaltyPointsRequestObject(state);
  await rbiIssueLoyaltyPointsRequest(requestObject);
};

export const processRbiIssueLoyaltyPointsAndRefund = () => async (dispatch, getState) => {
  const state = getState();
  const pointsRequestObject = constructIssueLoyaltyPointsRequestObject(state);
  const refundRequestObject = contructRefundRequestObject(state);

  const [approvedAmount] = await Promise.all([
    rbiRefundRequest(refundRequestObject),
    rbiIssueLoyaltyPointsRequest(pointsRequestObject),
  ]);

  dispatch(setRbiApprovedRefundAmount(approvedAmount));
};

// handles partial refund and coupon issue when free and paid items selected
export const processRbiRefundAndIssueCoupon = () => async (dispatch, getState) => {
  const state = getState();
  const refundRequestObject = contructRefundRequestObject(state);
  const couponRequestObject = constructCouponRequestObject(state);

  const [approvedAmount] = await Promise.all([
    rbiRefundRequest(refundRequestObject),
    rbiIssueCouponRequest(couponRequestObject),
  ]);

  dispatch(setRbiApprovedRefundAmount(approvedAmount));
};

// not in use just yet
export const getRbiAppliedOffersForCustomer = (simplrAccountId, email) => async (dispatch, getState) => {
  try {
    const state = getState();
    const brandName = getBrandOfSelectedTicket(state);
    const query = {
      simplrAccountId,
      email,
      brandName,
    };

    for (const param in query) {
      if (!query[param]) {
        throw new Error(`Missing param ${param}`);
      }
    }

    await rbiGetRequest('/offers/applied', query);
  } catch (error) {
    showExpressError(error, 'Error getting applied offers for this customer');
  }
};

export const getRbiCustomerInfoByEmail = (email) => async (dispatch, getState) => {
  const UNABLE_TO_GET_CARD_ERROR = 60;
  const USERS_NOT_FOUND_ERROR = 70;

  try {
    if (!emailRegex.test(email)) {
      displayPopupMessageInformation('Please enter a valid email address');

      return;
    }
    dispatch(setLoading(true, 'searchGiftCard'));
    const state = getState();
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const brandName = getBrandOfSelectedTicket(state);

    const params = {
      simplrAccountId,
      brandName,
      email,
    };

    const customer = await makeRequest(`${environment.eCommerce.RBI_BASE_URL}/customer`, { params });

    dispatch({
      type: SET_RBI_CUSTOMER_INFO,
      customer,
    });
  } catch (error) {
    let errorCode = null;
    try {
      const parsedErrorMsg = JSON.parse(error.message);
      errorCode = parsedErrorMsg.errorCode;
    } catch (e) {
      handleError({ error: e });
    }
    switch (errorCode) {
      case UNABLE_TO_GET_CARD_ERROR:
        dispatch(addEdgeToDecisionTreePath('card_not_found'));
        break;
      case USERS_NOT_FOUND_ERROR:
        dispatch(addEdgeToDecisionTreePath('no_account_found'));
        break;
      default:
        showExpressError(error, error.message);
    }
  } finally {
    dispatch(setLoading(false, 'searchGiftCard'));
  }
};

export const processRbiGiftCardTransferring = (cardNumber, email) => async (dispatch, getState) => {
  const USERS_NOT_FOUND_ERROR = 70;
  const GIFT_CARD_BALANCE_IS_ZERO_ERROR = 80;
  const GIFT_CARD_BALANCE_EXCEEDED_ERROR = 90;

  try {
    if (!emailRegex.test(email)) {
      displayPopupMessageInformation('Please enter a valid email address');

      return;
    }
    dispatch(setLoading(true, 'giftCardTransferring'));
    const state = getState();
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const brandName = getBrandOfSelectedTicket(state);
    const data = {
      simplrAccountId,
      brandName,
      cardNumber,
      email,
    };

    const transaction = await makeRequest(`${environment.eCommerce.RBI_BASE_URL}/cards`, {
      method: 'put',
      data,
    });

    dispatch({
      type: SET_RBI_GIFT_CARD_TRANSFER,
      transaction,
    });
    dispatch(addEdgeToDecisionTreePath('transfer_successful'));
  } catch (error) {
    let errorCode = null;
    try {
      const parsedErrorMsg = JSON.parse(error.message);
      errorCode = parsedErrorMsg.errorCode;
    } catch (e) {
      handleError({ error: e });
    }
    switch (errorCode) {
      case USERS_NOT_FOUND_ERROR:
        dispatch(addEdgeToDecisionTreePath('no_account_found'));
        break;
      case GIFT_CARD_BALANCE_IS_ZERO_ERROR:
        dispatch(addEdgeToDecisionTreePath('balance_is_zero'));
        break;
      case GIFT_CARD_BALANCE_EXCEEDED_ERROR:
        dispatch(addEdgeToDecisionTreePath('transfer_unsuccessful'));
        break;
      default:
        showExpressError(error, error.message);
    }
  } finally {
    dispatch(setLoading(false, 'giftCardTransferring'));
  }
};

export const rbiSetStores = (stores) => ({
  type: RBI_SET_STORES,
  stores,
});

export const rbiSetSelectedStore = (store) => ({
  type: RBI_SET_SELECTED_STORE,
  store,
});

export const rbiSetStoreLocationQuery = (query) => ({
  type: RBI_SET_STORE_LOCATION_QUERY,
  query,
});

export const rbiClearState = () => ({
  type: RBI_CLEAR_STATE,
});

export const getRbiStores = (query) => async (dispatch) => {
  let stores = [];
  try {
    dispatch(rbiSetStores([]));
    dispatch(rbiSetSelectedStore(null));
    dispatch(setLoading(true, 'searchPage'));
    const response = await rbiPostRequest('/store', query);
    stores = response.data.stores;
  } catch (err) {
    if (err && err.status === 404) {
      handleError({ error: err, customMessage: 'Error getting store locations' });

      return dispatch(rbiSetStores([]));
    }
    handleError({ error: err, customMessage: 'Error getting store locations. Please try again.', showPopup: true });
  } finally {
    dispatch(setLoading(false, 'searchPage'));

    return dispatch(rbiSetStores(stores));
  }
};

export const rbiUpdateCustomerBirthday = ({ simplrAccountId, birthday, customerId }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const brandName = getBrandOfSelectedTicket(state);

    const data = {
      simplrAccountId,
      brandName,
      customerId,
      birthday,
    };

    const response = await makeRequest(`${environment.eCommerce.RBI_BASE_URL}/update-customer-birthday`, {
      method: 'post',
      data,
    });

    await dispatch(setRbiCustomerBirthday(response.updateDob?.dob));
  };
};
